import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useBreakpoints, Breakpoints } from 'app/hooks';
import { Box, Flex, Loading } from 'components';
import { SendFeedbackProps, useContents, VideoNotes, VideoTranscription } from 'features/contents';
import { debounce, request, useQueryParameters } from 'app/utils';
import styled from 'styled-components';
import Player from 'features/player';
import { useSchool } from 'features/school';
import { TimelinePointState } from 'features/timeline';

import VideoTimeline from './videoTimeline';
import Comments, { useComments } from 'features/comments';

interface ContentVideoProps {
  sendFeedback: (props: SendFeedbackProps) => void;
}

interface WindowSize {
  innerWidth: number;
  innerHeight: number;
  videoWidth: number;
  videoHeight: number;
}

const VideoWrapper = styled(Flex)`
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MessagesTabs = styled(Flex)`
  flex-shrink: 0;
  height: 60px;
`;

const MessagesButton = styled.button`
  display: flex;
  align-items: center;
  position: relative;
  border: 0;
  background: none;
  color: white;
  text-transform: uppercase;
  font-size: 18px;
  padding: 0 40px;
  cursor: pointer;

  span {
    display: flex;
    align-items: center;
    background: ${({ theme }) => theme.colors.gray_600};
    font-size: 10px;
    margin-left: 10px;
    padding: 0 5px;
    border-radius: 4px;
  }

  ${(props: any) => {
    if (props['data-active']) {
      return `
        &::after {
          content: '';
          width: 100%;
          height: 4px;
          position: absolute;
          left: 0;
          z-index: 10;
          bottom: 0;
          background: ${props.theme.colors.primary_400};
        }
      `;
    }
  }}
`;

const { REACT_APP_SAVE_COMPLETE_PERCENTAGE } = process.env;

const ContentVideo = ({ sendFeedback, ...props }: ContentVideoProps) => {
  let query = useQueryParameters();

  const breakpoint = useBreakpoints();
  const [windowSize, setWindowSize] = useState<WindowSize | null>(null);

  const { contentDetails } = useContents();
  const { apiGatewayUrl } = useSchool();

  const {
    comments,
    commentsConfig: { enableComments },
  } = useComments();

  const currentPointSecond = contentDetails?.assessment?.videoCurrentPointInTime || 0;
  let isCompleted = contentDetails?.assessment?.isComplete;

  const completePercentage = Math.floor((Number(REACT_APP_SAVE_COMPLETE_PERCENTAGE) / 100) * (contentDetails?.video?.durationSeconds || 0));

  const getStartInSecond = useCallback(
    (receivedQuery): number => {
      if (receivedQuery.get('t')) return Number(receivedQuery.get('t'));

      if (currentPointSecond > completePercentage) return 0;

      return currentPointSecond || 0;
    },
    [currentPointSecond, completePercentage, isCompleted]
  );

  const [startInSecond, setStartInSecond] = useState(getStartInSecond(query));

  const [timelinePoint, setTimelinePoint] = useState<TimelinePointState>({
    seconds: 0,
    isLast: false,
  });

  const [savedAssessmentInSecond, setSavedAssessmentInSecond] = useState(0);

  const timelineSeconds = useCallback((): number[] => {
    return (
      contentDetails?.video?.timeline.reduce(
        (prev, timeline) => {
          return [...prev, Number(timeline.positionSeconds)];
        },
        [0]
      ) || []
    );
  }, [contentDetails]);

  const onTimeUpdate = useCallback(
    (seconds: number, savedAssessmentInSecond2: number) => {
      const videoSeconds = Math.floor(seconds);

      // Salva a evolução do vídeo
      let checkpoints = contentDetails?.video?.checkpoints || [];
      if (checkpoints.indexOf(videoSeconds) !== -1 && videoSeconds > savedAssessmentInSecond2) {
        setSavedAssessmentInSecond(videoSeconds);
      }

      // Define qual item da timeline vai ser exibido
      timelineSeconds().forEach((currentTimelineSeconds, index) => {
        const isLast = timelineSeconds.length - 1 === index;

        if (videoSeconds === currentTimelineSeconds && timelinePoint.seconds !== currentTimelineSeconds) {
          setTimelinePoint({
            seconds: currentTimelineSeconds,
            isLast,
          });
        }
      });

      // // Se passou do minimo para completude, seta como finalizado. PS: BACK já faz isso
      // if (!isCompleted && videoSeconds === saveCompletePercentage) {
      //   isCompleted = true;

      //   if (contentDetails?.video?.durationSeconds) {
      //     setSavedAssessmentInSecond(videoSeconds);
      //   }
      // }
    },
    [contentDetails, timelinePoint]
  );

  useEffect(() => {
    if (savedAssessmentInSecond) {
      request({
        url: `/api/v1/School/AssessContentVideoCurrentPointInTime`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          productClassUuid: contentDetails?.productClassUuid,
          contentUuid: contentDetails?.contentUuid,
          videoCurrentPointInTime: savedAssessmentInSecond,
        },
      });
    }
  }, [contentDetails, savedAssessmentInSecond]);

  const onEnd = useCallback(() => {
    setTimelinePoint({
      seconds: -1,
      isLast: true,
    });
  }, []);

  const playerGoTo = (seconds: number) => {
    setStartInSecond(seconds);

    setTimeout(() => {
      setStartInSecond(0);
    }, 1500);
  };

  const checkOnResize = useCallback(
    debounce(() => {
      if (breakpoint) setWindowSize(getWindowSize(breakpoint, 2));
    }, 150),
    [breakpoint]
  );

  useEffect(() => {
    checkOnResize();

    window.addEventListener('resize', checkOnResize);

    return () => {
      window.removeEventListener('resize', checkOnResize);
    };
  }, [breakpoint]);

  let initialCurrentTab = useMemo(() => {
    if (contentDetails?.video?.transcription) return 'transcription';

    if (enableComments) return 'comments';

    return 'notes';
  }, [contentDetails?.video?.transcription, enableComments]);

  const [currentTab, setCurrentTab] = useState(initialCurrentTab);

  useEffect(() => {
    if (enableComments) setCurrentTab('comments');
  }, [enableComments]);

  if (!contentDetails?.video || !windowSize) return <Loading label="Por favor, aguarde." />;

  const orientation = windowSize.innerWidth > windowSize.innerHeight ? 'portrait' : 'landscape';

  return (
    <Flex flexGrow={1} flexDirection={{ m: 'column', d: 'row' }} flexWrap="nowrap" bg="videoContentBg">
      <VideoWrapper
        data-videowrapper
        flexGrow={{ m: 0, d: 1 }}
        height={{ d: 'calc(100vh - 81px)' }}
        bg="dark_400"
        flexDirection="column"
        flexWrap="nowrap"
        overflowY={breakpoint === 'desktop' ? 'scroll' : 'inherit'}
        justifyContent="flex-start"
      >
        <Box data-videobox maxHeight={windowSize.videoHeight}>
          <Player
            onTimeUpdate={(seconds) => {
              onTimeUpdate(seconds, savedAssessmentInSecond);
            }}
            onEnd={onEnd}
            startInSecond={startInSecond}
            duration={contentDetails?.video?.durationSeconds}
            videoProvider={contentDetails?.video?.videoProvider}
            externalVideoProviderIdOrUrl={contentDetails && contentDetails?.video && contentDetails?.video.externalVideoProviderIdOrUrl}
            protectedVideoOtp={contentDetails && contentDetails?.video && contentDetails?.video.protectedVideoOtp}
            protectedVideoPlaybackInfo={contentDetails && contentDetails?.video && contentDetails?.video.protectedVideoPlaybackInfo}
            protectedVideoPlayerTheme={contentDetails && contentDetails?.video && contentDetails?.video.protectedVideoPlayerTheme}
            height={windowSize.videoHeight}
          />
        </Box>

        {(contentDetails?.video?.transcription || enableComments) && breakpoint === 'desktop' && (
          <Flex flexDirection="column" flexGrow={1}>
            <MessagesTabs flexGrow={0} bg="dark_400" px={{ default: 6 }}>
              {enableComments && (
                <MessagesButton onClick={() => setCurrentTab('comments')} data-active={currentTab === 'comments'}>
                  Comentários<span>{comments?.length}</span>
                </MessagesButton>
              )}

              {contentDetails?.video?.transcription && (
                <MessagesButton onClick={() => setCurrentTab('transcription')} data-active={currentTab === 'transcription'}>
                  Transcrição
                </MessagesButton>
              )}

              <MessagesButton onClick={() => setCurrentTab('notes')} data-active={currentTab === 'notes'}>
                Anotações
              </MessagesButton>
            </MessagesTabs>

            <Flex flexGrow={1} flexDirection="column">
              {currentTab === 'comments' && <Comments />}
              {currentTab === 'notes' && <VideoNotes name={contentDetails?.name} notes={contentDetails?.assessment?.notes} />}
              {currentTab === 'transcription' && (
                <VideoTranscription name={contentDetails?.name} transcription={contentDetails?.video?.transcription} />
              )}
            </Flex>
          </Flex>
        )}
      </VideoWrapper>

      <VideoTimeline
        timelinePoint={timelinePoint}
        playerGoTo={playerGoTo}
        sendFeedback={sendFeedback}
        orientation={orientation}
        windowSize={windowSize}
        breakpoint={breakpoint}
      />
    </Flex>
  );
};

export default ContentVideo;

const getWindowSize = (breakpoint: Breakpoints, from = 0): WindowSize => {
  const { innerWidth, innerHeight } = window;

  const videoWidth = breakpoint === 'desktop' ? Math.ceil(innerWidth - 425) : innerWidth;
  const videoHeight = Math.ceil(videoWidth / 1.775347912524851);

  return { innerWidth, innerHeight, videoWidth, videoHeight };
};
